import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Link, useParams } from "react-router-dom";
import materialInfo from "../Data.js";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Carousel } from "react-responsive-carousel";

const ProductDetail = () => {
  const params = useParams();
  const thisProduct = materialInfo.find((p) => p.id === parseInt(params.id));

  if (!thisProduct) {
    return (
      <Container>
        <NotFound>
          Product not found.{" "}
          <Link to="/products">Back to Products</Link>
        </NotFound>
      </Container>
    );
  }

  const specs = [
    ["Usage",     thisProduct.usage],
    ["Pattern",   thisProduct.pattern],
    ["Thickness", thisProduct.thickness],
    ["Color",     thisProduct.color],
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Container>
        {/* Breadcrumb */}
        <Breadcrumb>
          <Link to="/products">Products</Link>
          <BreadSep>/</BreadSep>
          <span>{thisProduct.name}</span>
        </Breadcrumb>

        {/* Page title */}
        <PageTitle>{thisProduct.name} &mdash; {thisProduct.fullform}</PageTitle>

        <Layout>
          {/* Left: image carousel */}
          <ImagePanel>
            <Carousel showThumbs={false} showStatus={false} infiniteLoop>
              {thisProduct.image.map((i) => (
                <ImageSlide key={i.ind}>
                  <img src={i.img} alt={thisProduct.name} />
                  {i.text && <SlideCaption>{i.text}</SlideCaption>}
                </ImageSlide>
              ))}
            </Carousel>
          </ImagePanel>

          {/* Right: details */}
          <DetailPanel>
            <ProductName>{thisProduct.name}</ProductName>
            <ProductFull>{thisProduct.fullform}</ProductFull>

            <Description>{thisProduct.description}</Description>

            {/* Spec table */}
            <SpecSection>
              <SpecTitle>Specifications</SpecTitle>
              <SpecTable>
                {specs.map(([label, value]) => (
                  <SpecRow key={label}>
                    <SpecKey>{label}</SpecKey>
                    <SpecVal>{value}</SpecVal>
                  </SpecRow>
                ))}
              </SpecTable>
            </SpecSection>

            {/* CTA */}
            <CTAPanel>
              <CTAText>Ready to place an order?</CTAText>
              <CTAGroup>
                <Link to="/contact">
                  <CTABtn>Request a Quote &rarr;</CTABtn>
                </Link>
                <Link to="/products" style={{ textDecoration: "none" }}>
                  <BackBtn>View All Products</BackBtn>
                </Link>
              </CTAGroup>
            </CTAPanel>
          </DetailPanel>
        </Layout>
      </Container>
    </motion.div>
  );
};

const Container = styled.div`
  padding: calc(var(--navbar-h) + 48px) 80px 80px;

  @media (max-width: 768px) {
    padding: calc(var(--navbar-h) + 24px) 24px 48px;
  }
`;

const NotFound = styled.p`
  text-align: center;
  padding: 80px 0;
  color: var(--color-muted);

  a {
    color: var(--color-brand);
    text-decoration: underline;
  }
`;

const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--color-muted);
  margin-bottom: 20px;

  a {
    color: var(--color-muted);
    text-decoration: none;
    transition: var(--transition);
    &:hover { color: var(--color-brand); }
  }

  span { color: var(--color-dark); font-weight: 500; }
`;

const BreadSep = styled.span`
  color: var(--color-border);
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: var(--color-dark);
  letter-spacing: -0.5px;
  margin: 0 0 48px;
  padding-bottom: 32px;
  border-bottom: 1px solid var(--color-border);

  @media (max-width: 768px) {
    font-size: 22px;
    margin-bottom: 32px;
  }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ImagePanel = styled.div`
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  overflow: hidden;
  background: var(--color-bg-subtle);

  img {
    width: 100%;
    height: 400px;
    object-fit: contain;
    display: block;
  }

  @media (max-width: 768px) {
    img { height: 260px; }
  }
`;

const ImageSlide = styled.div`
  position: relative;
`;

const SlideCaption = styled.p`
  font-size: 12px;
  color: var(--color-muted);
  text-align: center;
  padding: 8px 16px 12px;
  margin: 0;
  background: var(--color-bg);
  border-top: 1px solid var(--color-border);
`;

const DetailPanel = styled.div``;

const ProductName = styled.h2`
  font-size: 48px;
  font-weight: 900;
  color: var(--color-dark);
  letter-spacing: -2px;
  margin: 0 0 4px;
  line-height: 1;
`;

const ProductFull = styled.p`
  font-size: 14px;
  color: var(--color-muted);
  margin: 0 0 24px;
`;

const Description = styled.p`
  font-size: 15px;
  color: var(--color-muted);
  line-height: 1.8;
  margin: 0 0 36px;
  padding-bottom: 36px;
  border-bottom: 1px solid var(--color-border);
`;

const SpecSection = styled.div`
  margin-bottom: 36px;
`;

const SpecTitle = styled.h3`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--color-muted);
  margin: 0 0 16px;
`;

const SpecTable = styled.div`
  border: 1px solid var(--color-border);
  border-radius: 8px;
  overflow: hidden;
`;

const SpecRow = styled.div`
  display: flex;
  border-bottom: 1px solid var(--color-border);
  &:last-child { border-bottom: none; }
`;

const SpecKey = styled.div`
  background: var(--color-bg-subtle);
  width: 140px;
  padding: 13px 18px;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-dark);
  border-right: 3px solid var(--color-brand);
  flex-shrink: 0;
`;

const SpecVal = styled.div`
  padding: 13px 18px;
  font-size: 13px;
  color: var(--color-muted);
`;

const CTAPanel = styled.div`
  padding-top: 28px;
  border-top: 1px solid var(--color-border);
`;

const CTAText = styled.p`
  font-size: 13px;
  color: var(--color-muted);
  margin: 0 0 16px;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
`;

const CTAGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
`;

const CTABtn = styled.button`
  background: var(--color-dark);
  color: white;
  border: none;
  padding: 14px 32px;
  border-radius: var(--radius-btn);
  font-size: 14px;
  font-weight: 700;
  font-family: var(--font-primary);
  cursor: pointer;
  transition: var(--transition);
  letter-spacing: 0.5px;

  &:hover {
    background: var(--color-brand);
    transform: translateY(-2px);
  }
`;

const BackBtn = styled.button`
  background: transparent;
  color: var(--color-muted);
  border: 1px solid var(--color-border);
  padding: 13px 24px;
  border-radius: var(--radius-btn);
  font-size: 13px;
  font-weight: 500;
  font-family: var(--font-primary);
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    border-color: var(--color-dark);
    color: var(--color-dark);
  }
`;

export default ProductDetail;
